import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { userPanelApi } from 'api';
import { usePanel } from 'contexts/panelContext';
import { CreateGoalPayload, UpdateGoalPayload } from 'types/payloads/GoalPayload';

interface UseGoalsOptions {
	onMutationSuccess?: () => void;
}

interface ToggleGoalStepPayload {
	goalId: number;
	stepId: number;
}

const useGoals = (pairId: number, options?: UseGoalsOptions) => {
	const { currentProgramMembership, todosRefetch } = usePanel();
	const queryClient = useQueryClient();
	const { onMutationSuccess } = options ?? {};

	const {
		refetch: getGoals,
		data: fetchedGoals,
		isLoading: goalLoading,
		isError: goalError,
	} = useQuery({
		queryKey: ['goals', pairId],
		queryFn: () => userPanelApi.getGoals(+pairId, currentProgramMembership.id),
		enabled: !!currentProgramMembership?.id && !!pairId,
	});

	const { mutate: updateGoal } = useMutation({
		mutationFn: (goalPayload: UpdateGoalPayload) =>
			userPanelApi.updateGoal(+pairId, currentProgramMembership.id, goalPayload),
		onSuccess: async () => {
			await getGoals();
			onMutationSuccess?.();
		},
	});

	const { mutate: deleteGoal } = useMutation({
		mutationFn: (goalId: number) => userPanelApi.deleteGoal(+pairId, currentProgramMembership.id, goalId),
		onSuccess: async () => {
			await getGoals();
			onMutationSuccess?.();
		},
	});

	const { mutate: saveNewGoal } = useMutation({
		mutationFn: (goalPayload: CreateGoalPayload) =>
			userPanelApi.postGoal(+pairId, currentProgramMembership.id, goalPayload),
		onSuccess: async () => {
			await getGoals();
			onMutationSuccess?.();
		},
	});

	const { mutate: signGoals } = useMutation({
		mutationFn: () => userPanelApi.signGoal(+pairId, currentProgramMembership.id),
		onSuccess: async () => {
			await getGoals();
			todosRefetch();
			await queryClient.refetchQueries({ queryKey: ['sessions', { pairId: Number(pairId) }] });
		},
	});

	const { mutate: toggleGoalStep } = useMutation({
		mutationFn: ({ goalId, stepId }: ToggleGoalStepPayload) =>
			userPanelApi.toggleGoalStep(+pairId, currentProgramMembership.id, goalId, stepId),
		onSuccess: async () => {
			await getGoals();
		},
	});

	return {
		getGoals,
		updateGoal,
		deleteGoal,
		saveNewGoal,
		signGoals,
		toggleGoalStep,
		fetchedGoals,
		goalLoading,
		goalError,
	};
};

export default useGoals;
